import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useTesterIdentity } from "./TesterIdentityProvider";
import { isAuthErrorCode } from "./authErrors";
import SessionExpiredPanel from "./SessionExpiredPanel";

const STATUS_STYLES = {
  new: { bg: "#e3f2fd", fg: "#1565c0", label: "New" },
  reviewed: { bg: "#e8f5e9", fg: "#2e7d32", label: "Reviewed" }
};

const CATEGORY_LABELS = {
  workflow: "Workflow",
  copy: "Copy",
  "feature-request": "Feature request",
  other: "Other"
};

const formatRelative = (timestamp) => {
  if (!timestamp?.toMillis) {
    return "";
  }
  const diffMs = Date.now() - timestamp.toMillis();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const FeedbackRow = ({ feedback }) => {
  const style = STATUS_STYLES[feedback.status] || null;
  return (
    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
      <Typography variant="body2" sx={{ mb: 0.5, whiteSpace: "pre-wrap" }}>
        {feedback.description}
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        {feedback.category && CATEGORY_LABELS[feedback.category] ? (
          <Chip
            size="small"
            label={CATEGORY_LABELS[feedback.category]}
            sx={{ height: 20, fontSize: "0.65rem" }}
          />
        ) : null}
        {style ? (
          <Chip
            size="small"
            label={style.label}
            sx={{
              height: 20,
              fontSize: "0.65rem",
              fontWeight: 600,
              bgcolor: style.bg,
              color: style.fg
            }}
          />
        ) : null}
        <Typography variant="caption" color="textSecondary">
          {formatRelative(feedback.createdAt)}
        </Typography>
      </Stack>
    </Box>
  );
};

const isDismissed = (feedback) => feedback.status === "dismissed";

const FeedbackList = () => {
  const { internalDb, tester } = useTesterIdentity();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (!internalDb || !tester?.id) {
      setItems([]);
      setLoaded(false);
      setErrorCode(null);
      return undefined;
    }
    const feedbackQuery = query(
      collection(internalDb, "tester-feedback"),
      where("testerId", "==", tester.id)
    );
    const unsubscribe = onSnapshot(
      feedbackQuery,
      (snap) => {
        const docs = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));
        const visible = docs.filter((f) => !isDismissed(f));
        const sorted = visible.sort(
          (a, b) =>
            (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
        );
        setItems(sorted);
        setErrorCode(null);
        setLoaded(true);
      },
      (err) => {
        console.warn("[tester-chat] feedback subscription error:", err.message);
        setItems([]);
        setErrorCode(err.code || "unknown");
        setLoaded(true);
      }
    );
    return unsubscribe;
  }, [internalDb, tester?.id, retryToken]);

  if (!loaded) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Loading your feedback…
        </Typography>
      </Box>
    );
  }

  if (isAuthErrorCode(errorCode)) {
    return (
      <SessionExpiredPanel
        message="Your tester session expired. Sign in again to load your feedback."
        onReauthed={() => setRetryToken((prev) => prev + 1)}
      />
    );
  }

  if (errorCode) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="error" gutterBottom>
          Something went wrong loading your feedback.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Refresh the page to retry.
        </Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Nothing here yet. Feedback is for things that don't feel right but
          aren't broken — awkward copy, confusing steps, feature ideas.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Click "Send feedback" up top to add something. It goes straight to
          the product team — no automated back-and-forth.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {items.map((feedback) => (
        <FeedbackRow key={feedback.id} feedback={feedback} />
      ))}
    </Box>
  );
};

export default FeedbackList;
